import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useGooglePlaces } from "@/hooks/useGooglePlaces";

export interface VehicleWithPrice {
  id: string;
  name: string;
  capacity: number;
  min_passengers: number;
  badge: string | null;
  features: string[] | null;
  image_url: string | null;
  price: number | null;
  service_type_id: string | null;
}

export interface ServiceTypeGroup {
  id: string;
  name: string;
  vehicles: VehicleWithPrice[];
}

export function useVehiclePricing(
  from: string,
  to: string,
  fromPlaceId: string | undefined,
  toPlaceId: string | undefined,
  routeType: string = "one-way",
  passengers: number = 1
) {
  const [serviceGroups, setServiceGroups] = useState<ServiceTypeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [distanceKm, setDistanceKm] = useState<number | null>(null);
  const [durationMin, setDurationMin] = useState<number | null>(null);
  const [noServiceAvailable, setNoServiceAvailable] = useState(false);
  const { getDistanceMatrix } = useGooglePlaces();

  const isFromAirport = fromPlaceId?.startsWith("db-airport-");
  const isToAirport = toPlaceId?.startsWith("db-airport-");
  const isAirportRoute = isFromAirport || isToAirport;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setNoServiceAvailable(false);
    try {
      const [vehiclesRes, serviceTypesRes] = await Promise.all([
        supabase.from("transfer_vehicles").select("*").eq("is_active", true).order("sort_order"),
        supabase.from("service_types").select("*").eq("is_active", true).order("sort_order"),
      ]);

      const dbVehicles = vehiclesRes.data as any[];
      const dbServiceTypes = serviceTypesRes.data as any[];

      if (!dbVehicles?.length || !dbServiceTypes?.length) {
        setServiceGroups([]);
        setLoading(false);
        return;
      }

      const filteredVehicles = dbVehicles.filter(
        (v) => v.min_passengers <= passengers && passengers <= v.capacity
      );

      if (!filteredVehicles.length) {
        setServiceGroups([]);
        setLoading(false);
        return;
      }

      // Get distance
      let dist = 0;
      if (from && to) {
        const result = await getDistanceMatrix(from, to);
        if (result) {
          dist = result.distanceKm;
          setDistanceKm(dist);
          setDurationMin(result.durationMin);
        }
      }

      let priceMap: Record<string, number> = {};

      if (isAirportRoute) {
        const hotelPlaceId = isFromAirport ? toPlaceId : fromPlaceId;
        const airportPlaceId = isFromAirport ? fromPlaceId : toPlaceId;
        const hotelId = hotelPlaceId?.replace("db-hotel-", "");
        const airportId = airportPlaceId?.replace("db-airport-", "");

        if (hotelId && airportId) {
          const { data: hotelRecord } = await supabase
            .from("transfer_hotels")
            .select("name")
            .eq("id", hotelId)
            .maybeSingle();

          const hotelName = hotelRecord?.name;
          let zoneId: string | null = null;

          if (hotelName) {
            const { data: correctHotel } = await supabase
              .from("transfer_hotels")
              .select("zone_id")
              .eq("name", hotelName)
              .eq("airport_id", airportId)
              .maybeSingle();
            zoneId = (correctHotel as any)?.zone_id ?? null;
          }

          if (!zoneId) {
            setNoServiceAvailable(true);
            setServiceGroups([]);
            setLoading(false);
            return;
          }

          const { data: zonePrices } = await supabase
            .from("zone_pricing")
            .select("service_type_id, price, max_passengers, extra_cost_per_pax")
            .eq("zone_id", zoneId);

          if (!zonePrices || zonePrices.length === 0) {
            setNoServiceAvailable(true);
            setServiceGroups([]);
            setLoading(false);
            return;
          }

          const serviceTypePriceMap: Record<string, { price: number; max_passengers: number; extra_cost_per_pax: number }> = {};
          (zonePrices as any[]).forEach((zp) => {
            serviceTypePriceMap[zp.service_type_id] = {
              price: Number(zp.price),
              max_passengers: Number(zp.max_passengers),
              extra_cost_per_pax: Number(zp.extra_cost_per_pax),
            };
          });

          filteredVehicles.forEach((v) => {
            if (v.service_type_id && serviceTypePriceMap[v.service_type_id]) {
              const sp = serviceTypePriceMap[v.service_type_id];
              const extraPax = Math.max(0, passengers - sp.max_passengers);
              priceMap[v.id] = Math.round(sp.price + extraPax * sp.extra_cost_per_pax);
            }
          });
        }
      } else {
        const { data: rates } = await supabase
          .from("km_rates")
          .select("service_type_id, rate_per_km, min_price");

        if (rates && dist > 0) {
          const stPriceMap: Record<string, number> = {};
          (rates as any[]).forEach((r) => {
            if (r.service_type_id) {
              const calculated = dist * Number(r.rate_per_km);
              stPriceMap[r.service_type_id] = Math.round(Math.max(calculated, Number(r.min_price)));
            }
          });
          filteredVehicles.forEach((v) => {
            if (v.service_type_id && stPriceMap[v.service_type_id] !== undefined) {
              priceMap[v.id] = stPriceMap[v.service_type_id];
            }
          });
        }
      }

      const multiplier = routeType === "round-trip" || routeType === "roundtrip" ? 2 : 1;

      const vehiclesWithPrices: VehicleWithPrice[] = filteredVehicles.map((v) => ({
        id: v.id,
        name: v.name,
        capacity: v.capacity,
        min_passengers: v.min_passengers,
        badge: v.badge,
        features: v.features,
        image_url: v.image_url,
        price: priceMap[v.id] ? Math.round(priceMap[v.id] * multiplier) : null,
        service_type_id: v.service_type_id,
      }));

      const groups: ServiceTypeGroup[] = [];
      for (const st of dbServiceTypes) {
        const groupVehicles = vehiclesWithPrices.filter(
          (v) => v.service_type_id === st.id && v.price !== null
        );
        if (groupVehicles.length > 0) {
          groups.push({ id: st.id, name: st.name, vehicles: groupVehicles });
        }
      }

      setServiceGroups(groups);
    } catch (err) {
      console.error("Error fetching vehicles/pricing:", err);
    } finally {
      setLoading(false);
    }
  }, [from, to, fromPlaceId, toPlaceId, isAirportRoute, isFromAirport, getDistanceMatrix, routeType, passengers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { serviceGroups, loading, distanceKm, durationMin, noServiceAvailable };
}